import React, { useMemo } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { useNavigate, useSearchParams } from "react-router-dom";
import AgentDirectory from "@/components/AgentDirectory";
import DirectoryBrokerageSheet from "@/components/DirectoryBrokerageSheet";
import { marketPath } from "@/components/DirectoryBreadcrumb";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";
import useAvailableCities from "@/hooks/useAvailableCities";
import { getUserType } from "@/helpers/common";
import { USER_TYPE_AGENT } from "@/constants/common";
import { pageURLs } from "@/constants";
import "./DirectoryResults.scss";

export const filtersFromParams = (params) => ({
  withBuyers: params.get("buyers") === "1",
  q: params.get("q") || "",
  brokerage: params.get("brokerage") || "",
  network: params.get("network") || "",
});

export const isFiltered = (filters) => filters.withBuyers || Boolean(filters.q || filters.brokerage || filters.network);

export const widenedNote = (page, placeName) =>
  page?.widened && placeName ? `Showing agents within ${page.radiusMiles || 50} miles of ${placeName}` : "";

const Chevron = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FacetMenu = ({ label, value, allLabel, options, onChange }) => (
  <Dropdown className="directory-filters__menu">
    <Dropdown.Toggle as="button" type="button" className={`directory-filters__chip${value ? " is-on" : ""}`}>
      <span>{value || label}</span>
      <Chevron />
    </Dropdown.Toggle>
    <Dropdown.Menu className="directory-filters__list">
      <Dropdown.Item as="button" type="button" active={!value} onClick={() => onChange("")}>
        {allLabel}
      </Dropdown.Item>
      {options.map((option) => (
        <Dropdown.Item as="button" type="button" key={option.key} active={option.key === value} onClick={() => onChange(option.key)}>
          {option.label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const DirectoryResults = ({ location, placeSlug = "", placeName = "", scopeLabel = "" }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { featuredCities } = useAvailableCities();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const query = useAgentDirectory(location, filters, placeSlug);
  const first = query.data?.pages?.[0];
  const filtered = isFiltered(filters);
  const isAgentViewer = Number(getUserType()) === USER_TYPE_AGENT;
  const note = widenedNote(first, placeName);

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) =>
      value === null || value === "" || value === undefined || value === false ? next.delete(key) : next.set(key, String(value)),
    );
    setParams(next, { replace: true });
  };

  const clearFilters = () => update({ buyers: null, q: null, brokerage: null, network: null });
  const networks = first?.networks || [];
  const networkName = networks.find((item) => item.slug === filters.network)?.name || filters.network;
  const label = scopeLabel || [location?.city, location?.state].filter(Boolean).join(", ");

  return (
    <div className="directory-results">
      <div className="directory-filters">
        <button
          type="button"
          className={`directory-filters__chip${filters.withBuyers ? " is-on" : ""}`}
          aria-pressed={filters.withBuyers}
          onClick={() => update({ buyers: filters.withBuyers ? null : 1 })}
        >
          Working with buyers now
        </button>
        {location?.latitude ? (
          <DirectoryBrokerageSheet
            value={filters.brokerage}
            location={location}
            placeName={placeName}
            onChange={(name) => update({ brokerage: name })}
          />
        ) : null}
        {networks.length > 1 || filters.network ? (
          <FacetMenu
            label="Network"
            allLabel="All networks"
            value={filters.network ? networkName : ""}
            options={networks.map((item) => ({ key: item.slug, label: item.name }))}
            onChange={(slug) => update({ network: slug })}
          />
        ) : null}
        {filters.q ? (
          <button type="button" className="directory-filters__chip is-on" onClick={() => update({ q: null })}>
            “{filters.q}”
            <span aria-hidden="true" className="directory-filters__x">×</span>
            <span className="visually-hidden">Remove search</span>
          </button>
        ) : null}
        {filtered ? (
          <button type="button" className="directory-filters__clear" onClick={clearFilters}>
            Clear all
          </button>
        ) : null}
      </div>

      {note && !query.isLoading ? <p className="directory-results__note">{note}</p> : null}

      <AgentDirectory
        query={query}
        location={location}
        scopeLabel={label}
        placeName={placeName}
        filtered={filtered}
        onClearFilters={clearFilters}
        onSeeDemand={isAgentViewer ? undefined : () => navigate(pageURLs.home)}
        markets={featuredCities
          .filter((market) => market.displayName !== label)
          .map((market) => ({ ...market, href: marketPath(market) }))}
        onPickMarket={(market) => navigate(marketPath(market))}
      />
    </div>
  );
};

export default DirectoryResults;
